import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import useAuthStore from "../store/useAuthStore";
import useSupportStore from "../store/useSupportStore";
import useAddressStore from "../store/useAddressStore";

import { User, Loader2, ArrowLeft, HardHat, UserCircle2, ChevronRight } from "lucide-react";
import GenericModal from "../components/common/GenericModal";

const CustomerSupport = () => {
  const navigate = useNavigate();
  const { token, userType, user } = useAuthStore();
  const { createTicket, isSubmitting } = useSupportStore();
  const { districts, taluks, fetchMappedDistricts, fetchMappedTaluks } =
    useAddressStore();

  const [isOpen, setIsOpen] = useState(false);
  const [showFixedNav, setShowFixedNav] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    district: "",
    taluk: "",
    postcode: "",
    query: "",
  });
  const [errors, setErrors] = useState({});
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMappedDistricts("Tamil Nadu");
  }, [fetchMappedDistricts]);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector(".hero-section");
      if (heroSection) {
        setShowFixedNav(heroSection.getBoundingClientRect().bottom <= 0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (form.district) {
      fetchMappedTaluks(form.district, "Tamil Nadu");
    } else {
      setForm((prev) => ({ ...prev, taluk: "" }));
    }
  }, [form.district, fetchMappedTaluks]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    if (name === "phone") {
      // Only allow numbers and max 10 digits
      if (!/^\d*$/.test(value) || value.length > 10) {
        return;
      }
    }

    if (name === "postcode") {
      // Only allow numbers and max 6 digits
      if (!/^\d*$/.test(value) || value.length > 6) {
        return;
      }
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      newErrors.email = "Invalid email format";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(form.phone))
      newErrors.phone = "Phone must be 10 digits";
    if (!form.district) newErrors.district = "District is required";
    if (!form.taluk) newErrors.taluk = "Taluk is required";
    if (!form.postcode.trim()) newErrors.postcode = "Postcode is required";
    else if (!/^\d{6}$/.test(form.postcode))
      newErrors.postcode = "Postcode must be 6 digits";
    if (!form.query.trim()) newErrors.query = "Query is required";
    return newErrors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    // Add user type to form data
    const formData = {
      ...form,
      userType: selectedUserType
    };

    const result = await createTicket(formData);
    if (result.success) {
      setForm({
        name: "",
        email: "",
        phone: "",
        district: "",
        taluk: "",
        postcode: "",
        query: "",
      });
      setSelectedUserType("");
    }
  };

  const handleLoginRedirect = (selectedUserType) => {
    if (token && userType) {
      if (userType !== selectedUserType) {
        toast.error(`You are already logged in as a ${userType}`);
        return;
      }
      navigate(
        userType === "builder"
          ? "/builder/dashboard"
          : "/customer/projectinvite"
      );
    } else {
      navigate(`/login?type=${selectedUserType}`);
    }
  };

  const handleUserTypeSelect = (userType) => {
    setSelectedUserType(userType);
  };

  const handleBackToSelection = () => {
    setSelectedUserType("");
  };

  const navLinks = [
    { href: "/", text: "HOME" },
    { href: "/", text: "SERVICES" },
    { href: "#", text: "JOIN US", isModal: true },
    // { href: "/jobs", text: "JOB PORTAL" },
    { href: "/support", text: "SUPPORT" },
  ];

  const renderNavLinks = (isMobile = false, isFixed = false) =>
    navLinks.map((link, index) => (
      <a
        key={index}
        href={link.href}
        onClick={(e) => {
          e.preventDefault();
          if (link.isModal) {
            setIsSignupModalOpen(true);
          } else {
            navigate(link.href);
          }
          if (isMobile) setIsOpen(false);
        }}
        className={
          isMobile
            ? "block px-3 py-2 text-black font-medium hover:bg-gray-100 rounded-md"
            : `relative px-4 py-2 text-[14px] font-bold transition-all hover:rounded-full duration-300 ${isFixed
              ? "text-black hover:text-gray-500 hover:bg-gray-100"
              : "text-white hover:text-gray-300"
            }`
        }
      >
        {link.text}
      </a>
    ));

  const renderCustomerButton = (isMobile = false, isFixed = false) =>
    token && userType === "customer" && user ? (
      <button
        onClick={() => navigate("/customer/profile")}
        className={
          isMobile
            ? "flex items-center gap-2 w-full text-left px-3 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 rounded-md"
            : `flex items-center gap-2 bg-blue-600 text-white font-bold px-4 py-2 rounded-full hover:bg-blue-700`
        }
      >
        <User className="w-4 h-4" />
        Hello, {user.name.split(" ")[0]}
      </button>
    ) : (
      <button
        onClick={() => handleLoginRedirect("customer")}
        className={
          isMobile
            ? "block w-full text-left px-3 py-2 text-black font-medium hover:bg-gray-100 rounded-md"
            : "bg-blue-600 text-white font-bold px-6 py-2 rounded-full hover:bg-blue-700"
        }
        style={!isMobile && !isFixed ? { marginLeft: "60px" } : {}}
      >
        Customer Login
      </button>
    );

  return (
    <div className="w-full h-[100dvh] overflow-y-hidden">
      {/* Fixed Navigation */}
      {showFixedNav && (
        <div
          className="fixed top-0 left-0 right-0 bg-white shadow-md"
          style={{ zIndex: 9998 }}
        >
          <div className="max-w-[90rem] mx-auto px-2  sm:px-4 lg:px-6">
            <div className="flex justify-between items-center h-16">
              <div
                className="flex-shrink-0 cursor-pointer"
                onClick={() => navigate("/")}
              >
                <img
                  src="/assets/black logo.png"
                  alt="Ducktail"
                  className="h-12 sm:h-12"
                />
              </div>
              <div className="hidden lg:flex space-x-3 items-center">
                {renderNavLinks(false, true)}
                {renderCustomerButton(false, true)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section with Navigation */}
      <div
        className="hero-section relative w-full h-full flex items-start lg:items-center justify-center overflow-hidden py-4"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          {/* Desktop Background Image */}
          <img
            src="/assets/Customer-Support.jpg"
            alt="Customer Support"
            className="absolute inset-0 w-full h-full object-cover object-center hidden sm:block"
          />
          {/* Mobile Background Image */}
          <img
            src="/assets/support mobile.jpg"
            alt="Customer Support"
            className="absolute inset-0 w-full h-full object-cover object-center block sm:hidden"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Navigation Bar */}
        <div className="absolute top-0 left-0 right-0 z-30">
          <div className="bg-transparent">
            <div className="max-w-[90rem] mx-auto px-2 sm:px-4 lg:px-6">
              <div className="flex justify-between items-center h-16">
                <div
                  className="flex-shrink-0 cursor-pointer"
                  onClick={() => navigate("/")}
                >
                  <img
                    src="/assets/Transparent background.png"
                    alt="Ducktail"
                    className="h-12 sm:h-16 lg:h-20"
                  />
                </div>
                <div className="hidden lg:flex space-x-3 items-center">
                  {renderNavLinks()}
                  {renderCustomerButton()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 pb-8 pt-24 sm:pt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center">

            {/* Left Content - Hidden on mobile */}
            <div className="text-white space-y-4 lg:space-y-6 hidden lg:block">
              <div className="space-y-3 lg:space-y-4">
                <h1 className="text-xl md:text-2xl font-bold text-yellow-400 uppercase">
                  Are you stuck?
                </h1>
                <h2 className="text-base lg:text-lg xl:text-xl font-bold text-white">
                  Let Ducktail clear the way for you!
                </h2>
                <p className="text-sm lg:text-base xl:text-lg text-gray-200">
                  Submit your query today, and our expert team will help you navigate through any challenge with ease and expertise
                </p>
              </div>
            </div>

            {/* Right Side - Mobile Title + Form */}
            <div className="space-y-3 sm:space-y-4">
              {/* Title visible only on mobile - NO white background */}
              <div className="lg:hidden text-white space-y-1.5 sm:space-y-2">
                <h1 className="text-lg sm:text-xl font-bold text-yellow-400 uppercase">
                  Are you stuck?
                </h1>
                <p className="text-xs sm:text-sm text-white font-bold">
                  Let Ducktail clear the way for you!
                </p>
                <p className="text-xs sm:text-sm text-gray-200">
                  Submit your query today, and our expert team will help you navigate through any challenge with ease and expertise
                </p>
              </div>

              {/* Form Card */}
              <div
                className="w-full max-w-md mx-auto lg:mx-0 p-3 sm:p-4 lg:p-6 rounded-xl shadow-2xl relative overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)'
                }}
              >
                {!selectedUserType ? (
                  <div className="text-center">
                    <h3 className="text-base sm:text-lg font-bold text-white mb-4 sm:mb-5 tracking-wide">
                      I am a
                    </h3>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div
                        onClick={() => handleUserTypeSelect("builder")}
                        className="group flex flex-col items-center p-4 sm:p-5 border border-white/20 rounded-xl hover:border-blue-400/60 transition-all duration-300 cursor-pointer bg-white/5 hover:bg-white/15"
                        style={{ backdropFilter: 'blur(8px)' }}
                      >
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center mb-2.5 sm:mb-3 transition-all duration-300 group-hover:scale-110"
                          style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
                        >
                          <HardHat className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                        </div>
                        <h4 className="text-sm sm:text-base font-bold text-white mb-0.5">Builder</h4>
                        <p className="text-[10px] sm:text-xs text-white/60 leading-tight">Construction Professional</p>
                        <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-blue-400 mt-2 transition-all duration-300 group-hover:translate-x-0.5" />
                      </div>
                      <div
                        onClick={() => handleUserTypeSelect("customer")}
                        className="group flex flex-col items-center p-4 sm:p-5 border border-white/20 rounded-xl hover:border-blue-400/60 transition-all duration-300 cursor-pointer bg-white/5 hover:bg-white/15"
                        style={{ backdropFilter: 'blur(8px)' }}
                      >
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center mb-2.5 sm:mb-3 transition-all duration-300 group-hover:scale-110"
                          style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}
                        >
                          <UserCircle2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                        </div>
                        <h4 className="text-sm sm:text-base font-bold text-white mb-0.5">Customer</h4>
                        <p className="text-[10px] sm:text-xs text-white/60 leading-tight">Home Owner / Buyer</p>
                        <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-blue-400 mt-2 transition-all duration-300 group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="text-sm font-semibold text-white">
                        {selectedUserType === "builder" ? "Builder" : "Customer"} Support Form
                      </h3>
                      <button
                        onClick={handleBackToSelection}
                        className="flex items-center text-white/70 hover:text-white transition-colors text-sm"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                      </button>
                    </div>

                    <form onSubmit={handleFormSubmit} className="space-y-3 text-sm relative z-10">
                      <style>
                        {`
                          .modern-input {
                            width: 100%;
                            padding: 0.5rem 0.75rem;
                            height: 2.25rem;
                            background: rgba(255, 255, 255, 0.1);
                            border: 1px solid rgba(255, 255, 255, 0.3);
                            border-radius: 0.375rem;
                            color: white;
                            transition: all 0.3s ease;
                            font-size: 0.875rem;
                          }
                          .modern-input.textarea {
                            height: auto;
                            padding: 0.5rem 0.75rem;
                          }
                          .modern-input::placeholder {
                            color: rgba(255, 255, 255, 0.7);
                          }
                          .modern-input:focus {
                            outline: none;
                            border-color: #60a5fa;
                            background: rgba(255, 255, 255, 0.2);
                            box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.4);
                          }
                          .modern-input:disabled {
                            background: rgba(255, 255, 255, 0.05);
                            color: rgba(255, 255, 255, 0.5);
                            cursor: not-allowed;
                          }
                          .modern-select {
                            width: 100%;
                            padding: 0.5rem 2rem 0.5rem 0.75rem;
                            height: 2.25rem;
                            background: rgba(255, 255, 255, 0.1);
                            border: 1px solid rgba(255, 255, 255, 0.3);
                            border-radius: 0.375rem;
                            color: white;
                            transition: all 0.3s ease;
                            appearance: none;
                            background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23FFFFFF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.4-12.8z%22%2F%3E%3C%2Fsvg%3E");
                            background-repeat: no-repeat;
                            background-position: right 0.5rem top 50%;
                            background-size: 0.65rem auto;
                            font-size: 0.875rem;
                          }
                          .modern-select option {
                            background: #1e293b;
                            color: white;
                          }
                          .modern-select:focus {
                            outline: none;
                            border-color: #60a5fa;
                            background-color: rgba(255, 255, 255, 0.2);
                            box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.4);
                          }
                          .modern-select:disabled {
                            background-color: rgba(255, 255, 255, 0.05);
                            color: rgba(255, 255, 255, 0.5);
                            cursor: not-allowed;
                          }
                          .modern-btn {
                            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                            box-shadow: 0 4px 10px rgba(29, 78, 216, 0.3);
                            transition: all 0.3s ease;
                            font-weight: 500;
                          }
                          .modern-btn:hover:not(:disabled) {
                            transform: translateY(-1px);
                            box-shadow: 0 4px 12px rgba(29, 78, 216, 0.5);
                            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                          }
                          .modern-btn:active:not(:disabled) {
                            transform: translateY(1px);
                            box-shadow: 0 2px 8px rgba(29, 78, 216, 0.3);
                          }
                          .modern-error {
                            border-color: #ef4444 !important;
                            background-color: rgba(239, 68, 68, 0.1) !important;
                          }
                          .error-text {
                            color: #fca5a5;
                            font-size: 0.75rem;
                            margin-top: 0.125rem;
                            display: block;
                          }
                        `}
                      </style>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleInputChange}
                            placeholder="Full Name"
                            className={`modern-input ${errors.name ? "modern-error" : ""}`}
                          />
                          {errors.name && (
                            <span className="error-text">{errors.name}</span>
                          )}
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleInputChange}
                            placeholder="Email Address"
                            className={`modern-input ${errors.email ? "modern-error" : ""}`}
                          />
                          {errors.email && (
                            <span className="error-text">{errors.email}</span>
                          )}
                        </div>
                        <div>
                          <input
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            name="phone"
                            value={form.phone}
                            onChange={handleInputChange}
                            maxLength={10}
                            placeholder="Phone Number"
                            className={`modern-input ${errors.phone ? "modern-error" : ""}`}
                          />
                          {errors.phone && (
                            <span className="error-text">{errors.phone}</span>
                          )}
                        </div>
                        <div>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            name="postcode"
                            value={form.postcode || ""}
                            onChange={handleInputChange}
                            maxLength={6}
                            placeholder="Postcode"
                            className={`modern-input ${errors.postcode ? "modern-error" : ""}`}
                          />
                          {errors.postcode && (
                            <span className="error-text">{errors.postcode}</span>
                          )}
                        </div>
                        <div>
                          <select
                            name="district"
                            value={form.district}
                            onChange={handleInputChange}
                            className={`modern-select ${errors.district ? "modern-error" : ""}`}
                          >
                            <option value="">Select District</option>
                            {districts?.map((d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ))}
                          </select>
                          {errors.district && (
                            <span className="error-text">{errors.district}</span>
                          )}
                        </div>
                        <div>
                          <select
                            name="taluk"
                            value={form.taluk}
                            onChange={handleInputChange}
                            disabled={!form.district}
                            className={`modern-select ${errors.taluk ? "modern-error" : ""}`}
                          >
                            <option value="">Select Taluk</option>
                            {taluks?.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                          {errors.taluk && (
                            <span className="error-text">{errors.taluk}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <textarea
                          name="query"
                          value={form.query}
                          onChange={handleInputChange}
                          placeholder="How can we help you?"
                          rows="2"
                          className={`modern-input textarea resize-none ${errors.query ? "modern-error" : ""}`}
                        />
                        {errors.query && (
                          <span className="error-text">{errors.query}</span>
                        )}
                      </div>

                      <button
                        type="submit"
                        className="w-full text-white py-2 rounded-md flex items-center justify-center modern-btn disabled:opacity-70 disabled:cursor-not-allowed mt-1"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        {isSubmitting ? "Submitting..." : "Submit Query"}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      <GenericModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        title="Join Ducktail As"
        size="max-w-lg"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
          <div
            onClick={() => {
              setIsSignupModalOpen(false);
              navigate("/builder/signup");
            }}
            className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50"
          >
            <img src="/assets/builder.png" alt="Builder" className="w-24 h-24 object-contain mb-3" />
            <h3 className="text-md font-semibold text-gray-700">Builder</h3>
            <p className="text-xs text-gray-500 text-center mt-1">Showcase your projects and connect with clients.</p>
          </div>
          <div
            onClick={() => {
              setIsSignupModalOpen(false);
              navigate("/customer/signup");
            }}
            className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50"
          >
            <img src="/assets/young-man.png" alt="Customer" className="w-24 h-24 object-contain mb-3" />
            <h3 className="text-md font-semibold text-gray-700">Customer</h3>
            <p className="text-xs text-gray-500 text-center mt-1">Find verified builders for your dream project.</p>
          </div>
        </div>
      </GenericModal>
    </div>
  );
};

export default CustomerSupport;
